import type { FeatureCollection, LineString, MultiLineString, MultiPolygon, Point, Polygon } from "geojson";
import type { MapData } from "@/lib/navigation/types";
import { destination, distance, angleDifference } from "@/lib/navigation/simulator/geo";
import type { LocalizationResult, PlannedRoute, Scenario, SimulationDataset, StartArea } from "@/lib/navigation/simulator/types";

export interface SimulationOverlay {
    candidates: FeatureCollection<Point, { rank: number; weight: number; heading: number; stale: boolean }>;
    truth: FeatureCollection<Point>;
    route: FeatureCollection<LineString>;
    area: FeatureCollection<Polygon>;
    coverage: FeatureCollection<Polygon | MultiPolygon>;
    positionRegions?: FeatureCollection<MultiLineString>;
}

export function datasetMap(dataset: SimulationDataset): MapData {
    const counts = new Map<string, { total: number; verified: number }>();
    for (const landmark of dataset.landmarks) for (const binding of landmark.bindings) {
        const count = counts.get(binding.edgeId) ?? { total: 0, verified: 0 };
        count.total++; if (landmark.verified && !landmark.estimatedBinding) count.verified++;
        counts.set(binding.edgeId, count);
    }
    const names = new Map(dataset.edges.map(edge => [edge.id, edge.name]));
    return {
        roads: { type: "FeatureCollection", features: dataset.edges.map(edge => ({ type: "Feature", id: edge.id, geometry: edge.geometry,
            properties: { id: edge.id, name: edge.name, highway: "", oneWay: edge.oneWay, lengthMeters: edge.lengthMeters,
                landmarkCount: counts.get(edge.id)?.total ?? 0, verifiedCount: counts.get(edge.id)?.verified ?? 0 } })) },
        landmarks: { type: "FeatureCollection", features: dataset.landmarks.map(landmark => ({ type: "Feature", id: landmark.id,
            geometry: { type: "Point", coordinates: landmark.coordinate }, properties: {
                id: landmark.id, kind: landmark.kind, title: landmark.title, signCode: landmark.signCode, signValue: landmark.signValue,
                longitude: landmark.coordinate[0], latitude: landmark.coordinate[1], accuracyMeters: landmark.accuracyMeters, viewHeading: null,
                verified: landmark.verified && !landmark.estimatedBinding, source: "manual", sourceRef: null, notes: "", version: 1, updatedAt: "", verifiedAt: null,
                bindings: landmark.bindings.map(binding => ({ ...binding, roadName: names.get(binding.edgeId) ?? "", active: true, distanceMeters: 0 })),
            } })) }, roadsTruncated: false, landmarksTruncated: false,
    };
}

export function makeOverlay(dataset: SimulationDataset, route: PlannedRoute | null, scenario: Scenario | null,
    cursor: number, result: LocalizationResult, area: StartArea | null, showTruth: boolean): SimulationOverlay {
    const [w, s, e, n] = dataset.bounds;
    const truth = scenario?.steps[Math.max(0, cursor - 1)]?.truth;
    return {
        coverage: { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: dataset.coverageGeometry ?? { type: "Polygon", coordinates: [[[w,s],[e,s],[e,n],[w,n],[w,s]]] } }] },
        route: { type: "FeatureCollection", features: route && showTruth ? [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: route.coordinates } }] : [] },
        truth: { type: "FeatureCollection", features: truth && showTruth ? [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: truth.coordinate } }] : [] },
        candidates: { type: "FeatureCollection", features: result.candidates.map((candidate, index) => ({ type: "Feature",
            geometry: { type: "Point", coordinates: candidate.coordinate }, properties: { rank: index + 1, weight: candidate.weight, heading: candidate.heading, stale: candidate.stale } })) },
        area: { type: "FeatureCollection", features: area && area.center.every(Number.isFinite) && Math.abs(area.center[1]) <= 85 && Math.abs(area.center[0]) <= 180 ? [{ type: "Feature", properties: {}, geometry: { type: "Polygon",
            coordinates: [Array.from({ length: 65 }, (_, index) => destination(area.center, index * 360 / 64, area.radiusMeters))] } }] : [] },
    };
}

export function evaluateResults(results: LocalizationResult[], scenario: Scenario | null) {
    let firstFix: number | null = null, falseFixes = 0, fixes = 0, sumError = 0;
    let currentError: number | null = null;
    results.forEach((result, index) => {
        const candidate = result.candidates[0], truth = scenario?.steps[index]?.truth;
        if (!candidate || !truth) { currentError = null; return; }
        currentError = distance(candidate.coordinate, truth.coordinate);
        if (result.ignoredDuplicate) return;
        if (result.status !== "localized") return;
        const correct = currentError <= Math.max(25, candidate.uncertaintyMeters) && Math.abs(angleDifference(candidate.heading, truth.heading)) <= 40;
        if (correct && firstFix === null) firstFix = index + 1;
        if (!correct) falseFixes++;
        fixes++; sumError += currentError;
    });
    return { firstFix, falseFixes, currentError, meanFixError: fixes ? sumError / fixes : null };
}
